/**
 * Basket Route — Shopping basket with green theme
 *
 * Product list with images, prices, RRP, discount %, quantity controls,
 * and "Shop Pay" checkout button.
 *
 * @see Figma_assets/iOS/Basket@2x.png
 */

import { useState, useCallback } from 'react';
import { cn } from '@ownyou/ui-design-system';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface BasketItem {
  id: string;
  name: string;
  description: string;
  price: number;
  rrp: number;
  quantity: number;
}

// ---------------------------------------------------------------------------
// Sample data (replaced by real data in Sprint 15)
// ---------------------------------------------------------------------------

const INITIAL_BASKET: BasketItem[] = [
  {
    id: 'b1',
    name: 'Sensations Coated Peanuts - Thai Sweet Chilli',
    description: '150g packet',
    price: 1.0,
    rrp: 1.125,
    quantity: 1,
  },
  {
    id: 'b2',
    name: 'Lindt Excellence 85% Cocoa Dark Chocolate',
    description: '100g packet',
    price: 3.5,
    rrp: 4.0,
    quantity: 1,
  },
  {
    id: 'b3',
    name: 'Soreen Malt Bread',
    description: '5 pack',
    price: 1.25,
    rrp: 1.8,
    quantity: 1,
  },
  {
    id: 'b4',
    name: "Colman's Horseradish Sauce",
    description: '136g packet',
    price: 1.44,
    rrp: 1.8,
    quantity: 1,
  },
  {
    id: 'b5',
    name: 'Carrs Melts - Cheese',
    description: '150g packet',
    price: 1.55,
    rrp: 2.1,
    quantity: 1,
  },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDiscount(price: number, rrp: number): string {
  const discount = ((rrp - price) / rrp) * 100;
  return `${Math.round(discount * 10) / 10}% discount`;
}

// ---------------------------------------------------------------------------
// Basket item component
// ---------------------------------------------------------------------------

function BasketItemRow({
  item,
  onQuantityChange,
  onDelete,
}: {
  item: BasketItem;
  onQuantityChange: (id: string, delta: number) => void;
  onDelete: (id: string) => void;
}) {
  return (
    <div
      data-testid={`basket-item-${item.id}`}
      className="bg-slate-800 rounded-xl border border-slate-700 p-4"
    >
      <div className="flex gap-3">
        {/* Product info */}
        <div className="flex-1">
          <h3 className="text-sm font-semibold text-slate-100">{item.name}</h3>
          <p className="text-xs text-slate-400 mt-0.5">{item.description}</p>

          <div className="flex items-center gap-3 mt-2">
            <span className="text-base font-bold text-slate-100">
              £{item.price.toFixed(2)}
            </span>
            <span className="text-xs text-slate-500 line-through">
              RRP: £{item.rrp.toFixed(2)}
            </span>
            <span className="text-xs font-medium text-green-600">
              {formatDiscount(item.price, item.rrp)}
            </span>
          </div>

          {/* Quantity controls */}
          <div className="flex items-center gap-2 mt-3">
            <button
              type="button"
              onClick={() => onQuantityChange(item.id, 1)}
              className="w-7 h-7 rounded-full bg-emerald-500 text-white flex items-center justify-center text-lg font-bold"
              aria-label="Increase quantity"
            >
              +
            </button>
            <span className="text-sm font-medium text-slate-100 w-6 text-center">{item.quantity}</span>
            <button
              type="button"
              onClick={() => onQuantityChange(item.id, -1)}
              className="w-7 h-7 rounded-full bg-emerald-500 text-white flex items-center justify-center text-lg font-bold"
              aria-label="Decrease quantity"
            >
              -
            </button>
            <button
              type="button"
              onClick={() => onDelete(item.id)}
              className="ml-2 text-slate-500 hover:text-red-400 transition-colors"
              aria-label={`Remove ${item.name}`}
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
              </svg>
            </button>
          </div>
        </div>

        {/* Product image placeholder */}
        <div className="w-20 h-20 rounded-lg bg-slate-700 flex items-center justify-center flex-shrink-0">
          <svg className="w-8 h-8 text-slate-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <circle cx="8.5" cy="8.5" r="1.5" />
            <polyline points="21 15 16 10 5 21" />
          </svg>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function Basket() {
  const [items, setItems] = useState<BasketItem[]>(INITIAL_BASKET);

  const handleQuantityChange = useCallback((id: string, delta: number) => {
    setItems((prev) =>
      prev.map((item) =>
        item.id === id
          ? { ...item, quantity: Math.max(1, item.quantity + delta) }
          : item,
      ),
    );
  }, []);

  const handleDelete = useCallback((id: string) => {
    setItems((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <div
      data-testid="basket-screen"
      className={cn(
        'flex flex-col min-h-screen',
        'bg-emerald-500',
      )}
    >
      {/* Header */}
      <div className="text-center pt-4 pb-3">
        <p className="text-white text-sm italic">
          There are {totalItems} items in your basket
        </p>
      </div>

      {/* Items list */}
      <div className="flex-1 px-3 pb-24 space-y-3">
        {items.map((item) => (
          <BasketItemRow
            key={item.id}
            item={item}
            onQuantityChange={handleQuantityChange}
            onDelete={handleDelete}
          />
        ))}
      </div>

      {/* Shop Pay checkout button */}
      <div className="fixed bottom-20 left-0 right-0 px-4">
        <button
          type="button"
          data-testid="checkout-button"
          className={cn(
            'w-full py-4 rounded-xl',
            'bg-violet-600 text-white font-bold text-base',
            'flex items-center justify-center gap-2',
            'shadow-lg',
          )}
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
            <path d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 100 4 2 2 0 000-4z" />
          </svg>
          shop Pay
        </button>
      </div>
    </div>
  );
}

export default Basket;
